// E.164 phone normalisation — ONE definition, three callers.
//
// This rule lived as a private `phoneOf` inside apps/api/src/staff/query.ts. It
// was lifted here by portal-foundations (AC5) rather than copied, because the
// contact-phone backfill runs from scripts/ — outside the Worker — and a second
// implementation of a validation rule is precisely what
// scripts/check_source_of_truth.py fails the build over. apps/api imports
// toE164 now; the behaviour it enforces is byte-for-byte what it enforced
// before, pinned by the cases carried into tests/unit/phone.test.ts.

// Separators people actually type. Stripped before the shape is judged, so
// "+353 87 123 4567" and "+353-87-123-4567" are the same number.
const SEPARATORS = /[\s\-()]/g;

// A full international number: a leading +, a country code that cannot start
// with 0, then up to 14 more digits. E.164's own ceiling is 15 digits total.
const E164 = /^\+[1-9]\d{6,14}$/;

/**
 * The staff rule, unchanged. A blank or absent value is "no number on file
 * yet" — the gate the whole staff-brief messaging sequence waits on — and is
 * returned as null rather than treated as an error. Anything non-blank must
 * already name its country code.
 */
export function toE164(raw: string | null | undefined): string | null {
  if (typeof raw !== "string") return null;
  if (raw.trim() === "") return null;
  const stripped = raw.replace(SEPARATORS, "");
  return E164.test(stripped) ? stripped : null;
}

/**
 * The same rule, plus the one a spreadsheet needs.
 *
 * `owner_contacts.phone` was imported from Patricia's address book and is
 * overwhelmingly NATIONAL format — "087 123 4567". toE164 refuses every one of
 * those, so a backfill built on it alone would have run clean and normalised
 * nothing. This adds exactly two promotions and no guesses:
 *
 *   "00…" → "+…"        the international prefix is the + it stands for
 *   "0…"  → "+353…"     an Irish trunk 0, on an Irish kennel's address book
 *
 * A number with neither — a bare "871234567" — is REFUSED rather than assumed
 * to be Irish. It could be a mobile missing its trunk digit or it could be
 * anything, and this column is one the portal signs people in by: a wrong
 * number here is worse than a missing one.
 */
export function toE164Irish(raw: string | null | undefined): string | null {
  if (typeof raw !== "string") return null;
  const stripped = raw.replace(SEPARATORS, "");
  if (stripped === "") return null;

  if (stripped.startsWith("+")) return toE164(stripped);
  if (stripped.startsWith("00")) return toE164("+" + stripped.slice(2));
  if (stripped.startsWith("0")) return toE164("+353" + stripped.slice(1));
  return null;
}

/**
 * The number as the person reads it on their own phone — for the customer's
 * registration step and receipt (customer-sign-in-registration AC12, AC31).
 *
 * An Irish mobile in E.164 (`+353865550192`) is shown as the prefix `+353`
 * beside the national digits `86 555 0192`, and on the receipt as
 * `086 555 0192`. A number the backfill never reached is still in the
 * address book's own national format, and reads the same. A number from
 * anywhere else keeps its own country code as the prefix and is grouped
 * loosely — there is no rule for every country and guessing one is worse than
 * showing the digits.
 *
 * NEVER A GUESS: a bare local number with no trunk digit is shown as given,
 * with no prefix, because the same reasoning as `toE164Irish` applies — it
 * could be a mobile missing its 0 or it could be anything.
 */
export interface NationalPhone {
  /** `+353` — or `""` when the number carries no country code. */
  prefix: string;
  /** The national digits, grouped for reading: `86 555 0192`. */
  digits: string;
  /** The whole number as a person writes it: `086 555 0192`. */
  national: string;
}

function groupIrish(nsn: string): string {
  // 9 national digits: 2 + 3 + 4 — `86 555 0192`.
  if (nsn.length === 9) return `${nsn.slice(0, 2)} ${nsn.slice(2, 5)} ${nsn.slice(5)}`;
  return nsn;
}

/**
 * The ITU two-digit country codes. Everything else in those hundreds is three.
 *
 * This is a reading aid, not a claim about the country — the number is stored
 * and dialled whole either way. But `+97` and `+12` are not codes at all, and
 * printing one beside a number missing its first digit reads as corrupt data
 * to whoever is looking at it (P2.22, 2026-09-15).
 *
 * 1 (North America) and 7 (Russia, Kazakhstan) are the only one-digit codes
 * and are handled at the call site.
 */
const TWO_DIGIT_DIAL = new Set([
  "20", "27",
  "30", "31", "32", "33", "34", "36", "39",
  "40", "41", "43", "44", "45", "46", "47", "48", "49",
  "51", "52", "53", "54", "55", "56", "57", "58",
  "60", "61", "62", "63", "64", "65", "66",
  "81", "82", "84", "86",
  "90", "91", "92", "93", "94", "95", "98",
]);

export function toIrishNational(raw: string | null | undefined): NationalPhone | null {
  if (typeof raw !== "string") return null;
  const stripped = raw.replace(SEPARATORS, "");
  if (stripped === "") return null;

  if (stripped.startsWith("+353")) {
    const digits = groupIrish(stripped.slice(4));
    return { prefix: "+353", digits, national: `0${digits}` };
  }
  if (stripped.startsWith("0") && !stripped.startsWith("00")) {
    const digits = groupIrish(stripped.slice(1));
    return { prefix: "+353", digits, national: `0${digits}` };
  }
  if (stripped.startsWith("+")) {
    // Country code is 1–3 digits; the split is a reading aid, not a claim.
    //
    // THE TWO ONE-DIGIT CODES ARE NAMED (Codex review, PR #228). `\d{1,2}` is
    // greedy, so it always took two and rendered a US number `+12025550123` as
    // prefix `+12` — a country code that does not exist, beside a number
    // missing its first digit. E.164 has exactly two one-digit codes: 1 (North
    // America) and 7 (Russia, Kazakhstan). Everything else is two or three, and
    // this still shows two of them, which is the reading aid the line above
    // admits to rather than a claim about the country.
    // AND THE REST ARE TWO OR THREE, WHICH IS NOT A COIN TOSS. Taking two
    // whenever it was not 1 or 7 rendered `+971…` as prefix `+97` — again a
    // country code that does not exist, with the subscriber number starting on
    // the stray 1 (P2.22, 2026-09-15). E.164 assigns the two-digit codes
    // explicitly and everything else inside those hundreds is three digits, so
    // the set below decides it rather than the length of the match.
    const rest = stripped.slice(1);
    const two = rest.slice(0, 2);
    const len = rest[0] === "1" || rest[0] === "7" ? 1 : TWO_DIGIT_DIAL.has(two) ? 2 : 3;
    const nsn = rest.slice(len);
    if (nsn.length > 0) {
      const digits = nsn.length > 6 ? `${nsn.slice(0, 4)} ${nsn.slice(4)}` : nsn;
      return { prefix: `+${rest.slice(0, len)}`, digits, national: stripped };
    }
  }
  return { prefix: "", digits: stripped, national: stripped };
}
